import { inject, Injectable } from '@angular/core';
import { signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BrandSuggestionResponse } from '../../../onboarding/service/brand-fetch/brand-fetch.service';
import { email, form, maxLength, minLength, required } from '@angular/forms/signals';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { HttpClient } from '@angular/common/http';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { BusinessRegistrationTypeEnum, CreateBusinessAccountDto, CreateUserDto, UserRolesEnum } from '../../../../common/general-types/users.dto';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { IcpSearchService } from '../../../create-brief-basic/steps/creator-profile/service/icp-search/icp-search.service';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { BriefResponseDto } from '../../../../common/general-types/briefs.dto';

const BLOCKED_EMAIL_DOMAINS = new Set([
  // 'gmail.com', 'googlemail.com',
  // 'yahoo.com', 'ymail.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.fr', 'yahoo.de', 'yahoo.es',
  // 'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es',
  // 'outlook.com', 'outlook.co.uk',
  // 'live.com', 'live.co.uk',
  // 'msn.com',
  // 'icloud.com', 
  'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  // 'mailinator.com', 
  'mailsac.com',
  'guerrillamail.com', 'guerrillamail.info', 'guerrillamail.biz', 'guerrillamail.de', 'guerrillamail.net', 'guerrillamail.org',
  'tempmail.com', 'temp-mail.org',
  'throwaway.email',
  'sharklasers.com', 'grr.la',
  'spam4.me', 'trashmail.com', 'trashmail.me', 'trashmail.net',
  'dispostable.com', 'maildrop.cc',
  'yopmail.com', 'yopmail.fr',
]);

export function isBlockedEmailDomain(emailAddress: string): boolean {
  const domain = emailAddress.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return BLOCKED_EMAIL_DOMAINS.has(domain);
}

@Injectable({
  providedIn: 'root',
})
export class SlickOnboard {

  /**
   * Step 1.
   * User Details
   */
  userProfileModel = signal({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  /** Set after successful OTP verification — required to complete registration */
  verificationToken = signal<string | null>(null);
  userProfileForm = form(this.userProfileModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Email is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });

    required(schemaPath.password, { message: 'Password is required' });
    minLength(schemaPath.password, 8, { message: 'Password must be at least 8 characters long' });
    maxLength(schemaPath.password, 20, { message: 'Password must be less than 20 characters long' });

    required(schemaPath.firstName, { message: 'First name is required' });
    minLength(schemaPath.firstName, 2, { message: 'First name must be at least 2 characters long' });
    maxLength(schemaPath.firstName, 20, { message: 'First name must be less than 20 characters long' });

    required(schemaPath.lastName, { message: 'Last name is required' });
    minLength(schemaPath.lastName, 2, { message: 'Last name must be at least 2 characters long' });
    maxLength(schemaPath.lastName, 20, { message: 'Last name must be less than 20 characters long' });
  })

  /**
   * Step 2.
   * Brand Details
   */
  brandDetails = signal<BrandSuggestionResponse | undefined>(undefined);
  /** Business account `about` — Brandfetch descriptions or manual brand copy. */
  brandMarketingAbout = signal('');
  goalInformation = signal<{ id: number, name: string, description: string, color: string } | undefined>(undefined);
  /** Operating currency chosen by the brand during onboarding. Default USD until the visitor country is known. */
  brandCurrencyCode = signal<string>('usd');
  private brandCurrencyChosen = false;

  applyDetectedCountry(countryCode: string): void {
    if (this.brandCurrencyChosen) {
      return;
    }
    if (countryCode === 'GB') {
      this.brandCurrencyCode.set('gbp');
    }
  }

  chooseBrandCurrency(code: string): void {
    this.brandCurrencyChosen = true;
    this.brandCurrencyCode.set(code);
  }

  /**
   * Speedy Setup
   */

  /** Brief Details */
  promotingModel = signal({
    briefTitle: '',
    productLink: '',
  })
  promotingForm = form(this.promotingModel, (schemaPath) => {
    required(schemaPath.briefTitle, { message: 'Brief title is required' });
  })

  stepsHtml = signal<string>('');
  description = signal<string>('');
  stepsPlainText = signal<string>('');
  doHtml = signal<string>('');
  dontHtml = signal<string>('');
  doPlainText = signal<string>('');
  dontPlainText = signal<string>('');
  creativeConcepts = signal<{ id: number, name: string, description: string }[]>([]);
  

  /** Product Details */
  productDetailsModel = signal({
    productDescription: '',
    productImage: '',
    productName: '',
    productPrice: '',
    currency: '',
    productCategory: '',
    productIndustry: '',
  })
  productDetailsForm = form(this.productDetailsModel, (schemaPath) => {
    required(schemaPath.productName, { message: 'Product name is required' });
    required(schemaPath.productDescription, { message: 'Product description is required' });
    required(schemaPath.productImage, { message: 'Product image is required' });
    required(schemaPath.productPrice, { message: 'Product price is required' });
    required(schemaPath.currency, { message: 'Currency is required' });
  })

  newlyCreatedProduct = signal<UgcProductResponseDto | undefined>(undefined); 
  icpRecommendation = signal<{ json: any, paragraph: { sub_heading: string, paragraph: string }[] } | undefined>(undefined);
  newlyCreatedBrief = signal<BriefResponseDto | undefined>(undefined);

  /**
   * Speedy Setup - END
   */


  /** 
   * Creators to Invite
   */

  /** 
   * Creators to Invite - END
   */




  /** HubSpot tracking cookie — set early from onboarding-v2, re-read at signup as fallback */
  hubspotUserToken: string | undefined;

  /** Services */
  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  private currentUserService = inject(CurrentUserService);
  private icpSearchService = inject(IcpSearchService);
  private magicSearchService = inject(MagicSearchService);
  private document = inject(DOCUMENT);
  /** Services - END */

  async sendEmailOtp(turnstileToken: string | null, honeypot: string): Promise<void> {
    const url = `${environment.shout_api.url}/auth/send-email-otp`;
    await firstValueFrom(
      this.http.post(url, {
        email: this.userProfileModel().email,
        turnstileToken: turnstileToken ?? undefined,
        honeypot: honeypot || undefined,
      })
    );
  }

  async verifyEmailOtp(otp: string): Promise<void> {
    const url = `${environment.shout_api.url}/auth/verify-email-otp`;
    const result = await firstValueFrom(
      this.http.post<{ verificationToken: string }>(url, {
        email: this.userProfileModel().email,
        otp,
      })
    );
    this.verificationToken.set(result.verificationToken);
  }

  async registerBrandUser() {
    console.log('----> registerBrandUser()');

    const hubspotUserToken =
      this.hubspotUserToken ||
      this.getCookie('hubspotutk') ||
      '';

    const personalInfoReq: CreateUserDto = {
      firebaseUid: '',
      email: this.userProfileModel().email ?? 'no email...',
      firstName: this.userProfileModel().firstName ?? 'First',
      lastName: this.userProfileModel().lastName ?? 'Last',
      role: UserRolesEnum["Brand Admin"],
      jobPosition: 'unknown', // To-Do: Add job position
      photoURL: '',
      isActive: true,
      hubspotUserToken,
      verificationToken: this.verificationToken() ?? '',
    };

    if (this.userProfileModel().email && this.userProfileModel().password) {
      let deferredResult: unknown = false;

      this.firebaseService.beginDeferPostLoginBootstrap();
      try {
        const newUserResult = await firstValueFrom(this.firebaseService.signUp(this.userProfileModel().email, this.userProfileModel().password))

        if (!newUserResult.user) {
          console.error('Failed to create user 2');
          deferredResult = false;
        } else {
          personalInfoReq.firebaseUid = newUserResult.user.uid;
          const user_register_url = `${environment.shout_api.url}/business-accounts/onboarding-brand`
          const createUserResult = await firstValueFrom(
            this.http.post(user_register_url, personalInfoReq, {
              headers: {
                'Content-Type': 'application/json',
              }
            })
          )

          if (!createUserResult) {
            console.error('Failed to create user 1');
            deferredResult = null;
          } else {
            console.log('User created successfully: ', createUserResult);
            this.currentUserService.setShoutUser(createUserResult);

            const businessInfoReq: CreateBusinessAccountDto = {
              companyName: this.brandDetails()?.name ?? 'No name...',
              website: this.brandDetails()?.domain ?? 'No website...',
              instagram: '',
              tiktok: '',
              about: this.brandMarketingAbout() || '',
              coverPhoto: this.brandDetails()?.icon ?? 'No icon...',
              companyLogo: this.brandDetails()?.icon ?? 'No icon...',
              creatorId: personalInfoReq.firebaseUid,
              isActive: true,
              currencyCode: this.brandCurrencyCode(),
              isPayg: false,
              industries: [{
                emoji: this.brandDetails()?.icon ?? 'No icon...',
                id: this.brandDetails()?.brandId ?? 'No id...',
                name: this.brandDetails()?.name ?? 'No name...',
                slug: this.brandDetails()?.domain ?? 'No domain...',
                score: this.brandDetails()?.qualityScore ?? 0
              }],
              vatNumber: ''
            };

            const create_brand_url = `${environment.shout_api.url}/business-accounts/associate-brand-to-user`
            const createBrandResult = await firstValueFrom(
              this.http.post(create_brand_url, businessInfoReq, {
                headers: {
                  'Content-Type': 'application/json',
                }
              })
            )
            console.log('✅👉 Create Brand Result 2: ', createBrandResult);
            deferredResult = createBrandResult;
          }
        }

      } catch (signUpError) {
        console.error('Failed to create user 3: ', signUpError);
        deferredResult = false;
      } finally {
        this.firebaseService.endDeferPostLoginBootstrap();
      }

      if (deferredResult && deferredResult !== false) {
        // The brand association just created the BusinessUser and the backend rewrote
        // this user's Firebase custom claims (businessAccountId). Force a claims-sync +
        // token refresh BEFORE bootstrap so the first ClaimsGuard-protected request of the
        // session (e.g. GET /wallet/balance/:id) carries the new businessAccountId claim.
        await this.firebaseService.syncClaimsAndRefreshToken();
        this.firebaseService.runPostLoginBootstrapForCurrentUser();
      }

      return deferredResult;
    }

    return false;
  }

  private getCookie(name: string): string | undefined {
    const cookies = this.document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1, cookie.length);
      }
    }
    return undefined;
  }

  startCreatorPeronsa() {
    console.log('🧨🧨🧨 startCreatorPeronsa()');
    const brandName = this.currentUserService.focussedBrand.companyName;
    const briefTitle = this.promotingForm.briefTitle().value();
    const product = this.newlyCreatedProduct();
    const goal = this.goalInformation();

    console.log('🧨 brandName: ', brandName);
    console.log('🧨 briefTitle: ', briefTitle);
    console.log('🧨 product: ', product);
    console.log('🧨 goal: ', goal);

    if (brandName && briefTitle && product && goal) {
      this.icpSearchService.openAiCreatorProfileSearch(brandName, briefTitle, product, goal).then((response) => {
        console.log('🧨🧨🧨 ICP: ', response);

        this.icpRecommendation.set(response);

        this.findOnboardCreators();
        // this.findSomeCreators();
      });
    }
  }

  findOnboardCreators() {
    console.log('findOnboardCreators()');

    this.magicSearchService.selectedPointer.set({ label: 'lucky:', class: 'bg-pink-50 text-pink-500 ring-pink-700', description: "Tell us what you're looking for...", help: "Search for anything ... You're feeling lucky!" })
    const query = this.icpRecommendation()?.json.creator_persona ?? '';
    this.magicSearchService.searchQuery.set(query);

    this.magicSearchService.goldOnboardingSearchResults.set(undefined);
    this.magicSearchService.isSearching.set(true);
    this.magicSearchService.loadingResults.set(true);
    this.magicSearchService.kickOffGoldSearchOnboarding(
      query,
      0,
      20,
      this.newlyCreatedBrief()?.id,
    );
  }

  


}
